import React, { useState, useEffect, useId } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import { getInitials, getRandomColor } from '../../utils/UserCustomDisplay';
import PaginationControls from '../../utils/PaginationControls';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const FormUsersList = ({
  apiBaseUrl,
  selectedUsers,
  onSelectUser,
  ownerEmail = null,
  // Already-granted members (e.g. existing project members). Shown checked
  // and locked so re-adding them here can't silently overwrite their role.
  existingMemberIds = [],
  countryId = null,
  countryName = null
}) => {
  const instanceId = useId();

  // Users data
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);

  // Pagination and search states
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // null = no explicit tab choice yet — defaults to "country" once a country
  // is known, "all" otherwise. Once the user picks a tab it sticks.
  const [scope, setScope] = useState(null);
  const effectiveScope = scope ?? (countryId ? 'country' : 'all');
  const handleScopeChange = (newScope) => {
    setScope(newScope);
    setPage(1);
  };

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');

  const navigate = useNavigate();

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Fetch users on component mount and when pagination, search or the
  // active tab changes
  useEffect(() => {
    fetchUsers();
  }, [page, size, search, ownerEmail, effectiveScope, countryId]);

  // Fetch users from API
  const fetchUsers = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading users');

    try {
      // Create request body with pagination and search parameters
      const requestBody = {
        page: page,
        size: size,
        search: search || undefined,
        owner_email: ownerEmail || undefined,
        country_id: (effectiveScope === 'country' && countryId) ? Number(countryId) : undefined
      };
      
      const response = await axios.post(
        `${apiBaseUrl}/users-list`,
        requestBody,
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load users');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setUsers(data.data || []);
      setTotal(data.total || 0);
      
    } catch (error) {
      console.error('Error fetching users:', error);
      setPopupErrorMessage(error.message || 'Failed to load users');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1); // Reset to first page when search changes
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // stop form submission
      handleSearch();
    }
  };
  
  // Handle user selection. The full row is passed as a second argument for
  // callers that need to show the user's details back (existing callers take
  // only the id and are unaffected).
  const handleUserSelection = (userId, user) => {
    if (onSelectUser) {
      onSelectUser(userId, user);
    }
  };
  
  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };
  
  return (
    <div className="ev-card-3">
      {countryId && (
        <div className="btn-group custom-btn-group-outer mb-2" role="group" aria-label="User scope">
          <button
            type="button"
            className={`btn custom-btn-group-inner ${effectiveScope === 'country' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
            onClick={() => handleScopeChange('country')}
            aria-pressed={effectiveScope === 'country'}
          >
            {countryName ? `${countryName} users` : 'Country users'}
          </button>
          <button
            type="button"
            className={`btn custom-btn-group-inner ${effectiveScope === 'all' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
            onClick={() => handleScopeChange('all')}
            aria-pressed={effectiveScope === 'all'}
          >
            All users
          </button>
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="col-5">
          <div className="position-relative">
            <input
              type="text"
              className="form-control search-input ms-0"
              placeholder="Search users"
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
            />
            <button
              type="button"
              className="ev-btn-lightblue-fill-round search-bar-button"
              onClick={handleSearch}
            >
              <i className="bi bi-search"></i>
            </button>
          </div>
        </div>

        <div className="col-7 d-flex justify-content-end align-items-center">
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="ev-btn-light-fill ev-btn-xsmall"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <i className="bi bi-chevron-left"></i>
            </button>

            <span className="ev-xsmall-p-dark mx-2 text-nowrap">
              Page {page}/{Math.ceil(total / size) || 1}
            </span>

            <button
              type="button"
              className="ev-btn-light-fill ev-btn-xsmall"
              disabled={page >= Math.ceil(total / size)}
              onClick={() => setPage(page + 1)}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>

          <div className="d-flex align-items-center ms-2">
            <PaginationControls
              initialSize={size}
              onSizeChange={(newSize) => {
                setSize(newSize);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>
      
      <div className="scrollable-element vh-20">
        {users.length === 0 ? (
          <p className="ev-small-p-blue text-center mt-2">No users found</p>
        ) : (
          users.map(user => {
            const isExistingMember = existingMemberIds.includes(user.user_id);
            return (
            <div key={user.user_id} className="form-check mb-2 ms-1">
              <input
                className="form-check-input"
                type="checkbox"
                id={`${instanceId}-user-${user.user_id}`}
                checked={isExistingMember || selectedUsers.includes(user.user_id)}
                disabled={isExistingMember}
                onChange={() => handleUserSelection(user.user_id, user)}
              />
                <label className="form-check-label d-flex align-items-center" htmlFor={`${instanceId}-user-${user.user_id}`}>
                  <div
                    className="user-circle user-circle--small d-flex align-items-center justify-content-center me-2 ms-2"
                    style={{
                      backgroundColor: getRandomColor(user.email),
                    }}
                    title={user.email}
                  >
                    {getInitials(user.email)}
                  </div>
                  <p className="mb-0 ev-small-p-dark">
                    {user.email}
                    {isExistingMember && (
                      <span className="ev-message-card1 ev-btn-xsmall ms-2">Already a member</span>
                    )}
                  </p>
                </label>
            </div>
            );
          })
        )}
      </div>
      
      <div className="mt-2">
        {countryId ? (
          <p className="ev-small-p-dark mb-0">
            {selectedUsers.length} selected in total
            <span
              className="avatar-tooltip avatar-tooltip-start ms-1"
            >
            </span>
          </p>
        ) : (
          <p className="ev-small-p-dark mb-0">
            Selected: {selectedUsers.length} {selectedUsers.length === 1 ? 'user' : 'users'}
          </p>
        )}
      </div>
      
      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
    </div>
  );
};

export default FormUsersList;